import { ActivatedRoute } from '@angular/router';
import { NgxButtonComponent } from '@digipay/ngx-button';

import { Component, inject, OnInit, signal } from '@angular/core';
import { RouteStateService } from '@client-monorepo/common/utilities';
import { isValidIBANNumber } from '../../../../components/utils/strings';
import { UiFormFieldBuilderModule } from '@digipay/ui-form-field-builder';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { WALLET_CASH_OUT_ROUTE } from '../../../../data-access/constants/app-routes';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { WalletService } from '../../services/wallet.service';
import { IWalletProcess } from '../../models/wallet-cashin-model.interface';
import { WALLET_WITHDROW_PROCESS_API } from '../../../../data-access/constants/api';
import { IProcessData } from '../../models/wallet-process.interface';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { BaseComponent } from '../../../../components/core/components/base/base.component';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'wealth-applet-wallet-iban',
  standalone: true,
  imports: [NgxAppBarComponent, UiFormFieldBuilderModule, ReactiveFormsModule, NgxButtonComponent, SpinnerComponent],
  styleUrl: './wallet-iban.component.scss',
  templateUrl: './wallet-iban.component.html',
})
export class WalletIbanComponent extends BaseComponent implements OnInit {
  form!: FormGroup;
  walletId = signal<string | undefined>(undefined);
  isLoading = signal<boolean>(false);
  state = signal<IProcessData | undefined>(undefined);
  checkSejamLoading = signal<boolean>(false);
  parentErrors = signal<{
    [key: string]: string;
  }>({});

  private formBuilder = inject(FormBuilder);
  private walletService = inject(WalletService);
  private routeState = inject(RouteStateService);
  private activatedRoute = inject(ActivatedRoute);
  private navigationService = inject(WealthNavigationService);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.state.set(this.routeState.getAll());
    this.walletId.set(this.activatedRoute.snapshot.params['id']);
    if (!this.state()?.nationalId) {
      this.navigationService.navigate([WALLET_CASH_OUT_ROUTE, this.walletId()]);
    }
    this.createForm({ iban: '' });
  }

  private resetValidators(): void {
    this.form
      .get('iban')
      .valueChanges.pipe(takeUntil(this.destroyObservable))
      .subscribe((value) => {
        if (value.length <= 23) {
          this.parentErrors.set({});
          this.updateValidityOfErrorFields();
        }
      });
  }

  onBackHandler() {
    this.navigationService.navigate([WALLET_CASH_OUT_ROUTE, this.walletId()], {
      state: {
        ...this.state(),
      },
    });
  }

  serverErrorValidator(controlName: string): ValidatorFn {
    return (): ValidationErrors | null => {
      return this.parentErrors()[controlName] ? { serverError: true } : null;
    };
  }

  checkSejam() {
    this.checkSejamLoading.set(true);
    const processData: IWalletProcess = {
      walletId: this.walletId(),
      walletName: this.state()?.walletName,
      shebaNumber: this.form?.controls['iban'].value,
      amount: this.state()?.amount,
      walletUncollectibleBalance: this.state()?.walletUncollectibleBalance,
      walletWithdrawableBalance: this.state()?.walletWithdrawableBalance,
    };

    this.walletService.walletProcess(WALLET_WITHDROW_PROCESS_API, processData).subscribe((res) => {
      if (res && res.result && res.result.action === 'error') {
        this.parentErrors.set({
          iban: res.result.data.message,
        });
        this.updateValidityOfErrorFields();

        setTimeout(() => {
          this.resetValidators();
        }, 1000);
      }
      this.checkSejamLoading.set(false);
    });
  }

  updateValidityOfErrorFields() {
    Object.keys(this.parentErrors()).forEach((item) => {
      this.form.get(item).updateValueAndValidity();
      this.form.get(item).markAsDirty();
    });
  }

  private ibanChecksumValidator(control: FormControl): { [p: string]: boolean } | null {
    if (isValidIBANNumber(this.cleanIban(control.value))) {
      return null;
    }
    return { invalidIban: true };
  }

  private cleanIban(value: string) {
    if (!value) {
      return value;
    }
    return value.replace('-', '').replace(/\s/gi, '');
  }

  private createForm(preFillData: { iban: string }): void {
    this.form = this.formBuilder.group({
      iban: [
        preFillData.iban,
        [Validators.required, Validators.pattern(/IR\d{24}$/i), this.serverErrorValidator('iban'), this.ibanChecksumValidator.bind(this)],
      ],
    });
    if (preFillData.iban) {
      this.form.controls['iban'].markAsTouched();
    }
  }
}
